/**
 * O CSV do catálogo WRICEF.
 *
 * Mora fora do componente porque a ordem das colunas é contrato com quem abre
 * o arquivo numa planilha, e contrato afirmável sem montar tela. Exportar uma
 * função de um arquivo de componente também tira o Fast Refresh dele.
 */
import { CSV_BOM } from "@/lib/csv";
import { getCriticalityLabel, getReviewStatus } from "@/lib/wricef-status";
import type { WricefCatalogItem } from "@/types/wricef";

const CSV_HEADER = [
  "obj_name",
  "obj_type_tadir",
  "wricef",
  "wricef_fallback",
  "devclass",
  "module",
  // #922: o módulo da coluna "Module", e se ele veio do pacote.
  "resolved_stream",
  "stream_inherited",
  "author",
  // #906: ao lado do autor, e não no fim. As duas colunas respondem a mesma
  // pergunta ("de quem é e de quando é"), e quem abre o CSV lê as duas juntas.
  "created_on",
  "criticality_score",
  "criticality_level",
  "clean_core_level",
  "review_status",
  "source",
  "description_pt",
  "scope_item_candidato",
  "conversion_tipo",
  // A proposta de solução da aba Diagnóstico do objeto: é o texto que a
  // revisão em planilha existe para conferir.
  "conversion_descricao",
  "conversion_esforco_h",
  "analysis_confidence",
  "eol_count",
  "code_storage_path",
  // #1500: a decisão do arquiteto, ao lado do que a IA sugeriu, para a revisão
  // em planilha cruzar as duas. No fim, e não perto das colunas de conversão:
  // quem já tem planilha montada sobre este arquivo não perde a posição de
  // nenhuma coluna que já existia.
  "decision",
  "decision_note",
  "decision_standard_scope_item",
  "decision_strategy_suggested",
  "decision_strategy",
  "decision_hours_suggested",
  "decision_hours",
  "decision_functional_description",
];

/**
 * O CSV do catálogo, como texto. Exportado para ser afirmável sem montar a
 * tela: a ordem das colunas é contrato com quem abre isto numa planilha, e
 * campo novo entrando no lugar errado passa despercebido num teste de clique.
 */
export function buildCatalogCsv(rows: WricefCatalogItem[]): string {
  const escape = (v: unknown): string => {
    const s = v == null ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // O BOM é o que faz o Excel abrir o arquivo como UTF-8; sem ele os acentos
  // das descrições chegam quebrados.
  return (
    CSV_BOM +
    CSV_HEADER.join(";") +
    "\n" +
    rows
      .map((r) =>
        [
          r.obj_name,
          r.obj_type_tadir,
          r.wricef,
          r.wricef_fallback,
          r.devclass ?? "",
          r.module ?? "",
          r.resolved_stream ?? "",
          r.stream_inherited,
          r.author ?? "",
          // Ausência vira campo vazio, e nunca o rótulo da tela: "Not dated"
          // numa coluna de data quebra quem abre isto numa planilha.
          r.created_on ?? "",
          r.criticality_score ?? "",
          getCriticalityLabel(r.criticality_score).label,
          r.clean_core_level ?? "",
          getReviewStatus(r).label,
          r.source ?? "",
          r.description_pt ?? "",
          r.scope_item_candidato ?? "",
          r.conversion_tipo ?? "",
          r.conversion_descricao ?? "",
          r.conversion_esforco_h ?? "",
          r.analysis_confidence ?? "",
          r.eol_signals?.length ?? 0,
          r.code_storage_path ?? "",
          // Tokens crus (`in_scope`, `developer`), como o `source` e como o CSV
          // das decisões (`GET .../wricef-decisions?format=csv`), para os dois
          // arquivos se cruzarem.
          r.decision ?? "",
          r.decision_note ?? "",
          r.decision_standard_scope_item ?? "",
          r.decision_strategy_suggested ?? "",
          r.decision_strategy ?? "",
          // A hora sugerida é a da análise: a view a chama de `hours_suggested`.
          r.conversion_esforco_h ?? "",
          r.decision_hours ?? "",
          r.decision_functional_description ?? "",
        ]
          .map(escape)
          .join(";"),
      )
      .join("\n")
  );
}
